import type {
    BaseStageData,
    ComponentStageData,
    ConversionDal,
    PluginContext,
    PluginPostStageHandler,
    PluginStageHandler,
    ReadOnlyExtensionAPI,
    VirtualStructureEntryItem
} from '../../../../types'
import {
    getMeasurementsPointer,
    getSourceLayoutPointer,
    getStructurePointer,
    getVirtualStructurePointer
} from '../../../../conversionDal/utils/pointer'
import {isHeaderContainer, isPinned, isSection, updateTargetLayout} from '../../../common'
import {
    CompareFunction,
    generateChildLayout,
    generatePinnedChildLayout,
    getContainerColumnCountKey,
    getContainerRowCountKey,
    getOrderStrategy,
    sortHeaderContainerChildren
} from '../../common'
import type {LayoutIntent, StructureEntryItem} from '../../../../conversionDal/types'
import type {Pointer, SingleLayoutData} from '@wix/document-services-types'
import {AutoResponsivenessMode} from '../../../../../../constants/dsTypesConstants'
import {SkipPredicate, VirtualStructureTag} from '../../../../enums'
import {
    HEADER_NO_DUPLICATE_WIDTH_THRESHOLD,
    HEADER_SPECIAL_CASE_KEY,
    ORDERED_GROUPED_STRUCTURE_HASH_KEY,
    ORDERED_GROUPED_STRUCTURE_KEY
} from '../../constants'
import {md5} from '@wix/document-manager-utils'
import {SemanticTypes} from '../../../../preprocess/conversionDataBuilders'

type RowPredicateFactory = (
    startRow: number,
    startColumn: number
) => (childId: string) => {row: number; column: number; columnEnd?: number}

const updateMapWithChildren = (
    contextHelper: PluginContext,
    containerId: string,
    data: {
        tag?: string
        children: string[]
        layout?: SingleLayoutData
    }
) => {
    const currentStructure = contextHelper.get(ORDERED_GROUPED_STRUCTURE_KEY) ?? []
    currentStructure.push([containerId, data])
    contextHelper.set(ORDERED_GROUPED_STRUCTURE_KEY, currentStructure)
}

const processChildren = (
    children: string[],
    conversionDal: ConversionDal,
    containerPointer: Pointer,
    contextHelper: PluginContext,
    getRowAndColumnPredicate: RowPredicateFactory,
    minColumnCount: number = 1
) => {
    let maxStartRow = 1
    let maxStartColumn = minColumnCount
    const rowColumnPredicateFunc = getRowAndColumnPredicate(maxStartRow, maxStartColumn)

    for (const childId of children) {
        const childPointer = getStructurePointer({id: childId})

        if (isPinned({conversionDal, componentPointer: childPointer})) {
            updateTargetLayout({conversionDal, componentPointer: childPointer}, generatePinnedChildLayout())
        } else {
            const {row: startRow, column: startColumn, columnEnd} = rowColumnPredicateFunc(childId)
            maxStartRow = Math.max(maxStartRow, startRow)
            maxStartColumn = Math.max(maxStartColumn, startColumn)
            updateTargetLayout(
                {conversionDal, componentPointer: childPointer},
                generateChildLayout(startRow, startColumn, columnEnd)
            )
        }
    }

    const rowCountKey = getContainerRowCountKey(containerPointer)
    const columnCountKey = getContainerColumnCountKey(containerPointer)
    contextHelper.set(rowCountKey, maxStartRow)
    contextHelper.set(columnCountKey, maxStartColumn)
}

const getRealChildrenAfterOrder = (
    conversionDal: ConversionDal,
    virtualStructurePointer: Pointer,
    contextHelper: PluginContext,
    realChildren: string[] = []
): string[] => {
    const virtualStructure = conversionDal.get(virtualStructurePointer)

    if (virtualStructure) {
        updateMapWithChildren(contextHelper, virtualStructurePointer.id, {
            children: virtualStructure.components,
            tag: virtualStructure.tag
        })
        for (const childId of virtualStructure.components) {
            const virtualChildPointer = getVirtualStructurePointer({id: childId})
            const child = conversionDal.get(virtualChildPointer)

            if (child.tag === VirtualStructureTag.Virtual) {
                getRealChildrenAfterOrder(conversionDal, virtualChildPointer, contextHelper, realChildren)
            } else {
                realChildren.push(childId)
            }
        }
    }

    return realChildren
}

const sortComponentsByOrder = (
    componentPointer: Pointer,
    conversionDal: ConversionDal,
    compareFunction: CompareFunction,
    getSortValue: (childId: string) => any
): string[] => {
    const {components: originalComponents} = conversionDal.get(componentPointer)
    return [...(originalComponents ?? [])].sort((childIdA, childIdB) =>
        compareFunction(getSortValue(childIdA), getSortValue(childIdB))
    )
}

export const createOrderVirtualStructure: (
    readOnlyExtensionAPI: ReadOnlyExtensionAPI
) => PluginStageHandler<ComponentStageData> =
    readOnlyExtensionAPI =>
    ({componentPointer, conversionDal}: ComponentStageData) => {
        const virtualStructureEntry = conversionDal.get(componentPointer)
        let layoutIntent: LayoutIntent | undefined

        if (virtualStructureEntry?.tag !== VirtualStructureTag.Virtual) {
            const structureEntry = conversionDal.get(getStructurePointer(componentPointer))
            layoutIntent = structureEntry?.layoutIntent
        }

        const isRtl = readOnlyExtensionAPI.isRTL()
        const {compareFunction, appliedHeuristics} = getOrderStrategy(layoutIntent, isRtl)

        const components = sortComponentsByOrder(
            getVirtualStructurePointer(componentPointer),
            conversionDal,
            compareFunction,
            childId =>
                (conversionDal.get(getVirtualStructurePointer({id: childId})) as VirtualStructureEntryItem).measurements
        )

        conversionDal.modify(getVirtualStructurePointer(componentPointer), value => ({
            ...value,
            components
        }))

        return {appliedHeuristics}
    }

const sortRealStructure = (
    readOnlyExtensionAPI: ReadOnlyExtensionAPI,
    {componentPointer, conversionDal}: ComponentStageData
): string[] => {
    const structureEntry = conversionDal.get(componentPointer)
    const layoutIntent = structureEntry?.layoutIntent

    const isRtl = readOnlyExtensionAPI.isRTL()
    const {compareFunction} = getOrderStrategy(layoutIntent, isRtl)

    return sortComponentsByOrder(componentPointer, conversionDal, compareFunction, childId =>
        conversionDal.get(getMeasurementsPointer({id: childId}))
    )
}

const getOrderedChildren = (
    componentStageData: ComponentStageData,
    contextHelper: PluginContext,
    readOnlyExtensionAPI: ReadOnlyExtensionAPI
): string[] => {
    const {componentPointer, conversionDal, config} = componentStageData
    const virtualStructurePointer = getVirtualStructurePointer(componentPointer)
    const virtualStructure = conversionDal.get(virtualStructurePointer)
    const isInsideHeader = isHeaderContainer({conversionDal, componentPointer})

    if (virtualStructure) {
        return getRealChildrenAfterOrder(conversionDal, virtualStructurePointer, contextHelper)
    }

    const sortedComponents = isInsideHeader
        ? sortComponentsByOrder(
              componentPointer,
              conversionDal,
              sortHeaderContainerChildren(
                  conversionDal,
                  readOnlyExtensionAPI.hasDuplicatedComponent(config.rootPointer)
              ),
              childId => childId
          )
        : sortRealStructure(readOnlyExtensionAPI, componentStageData)
    updateMapWithChildren(contextHelper, componentPointer.id, {children: sortedComponents})

    return sortedComponents
}

export const orderRealStructure =
    (readOnlyExtensionAPI: ReadOnlyExtensionAPI) =>
    (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
        const {componentPointer, config, conversionDal} = componentStageData
        const component = conversionDal.get(componentPointer)

        if (componentPointer.id === config.rootPointer.id) {
            updateMapWithChildren(contextHelper, `ROOT-${componentPointer.id}`, {
                children: [component.id],
                layout: !isSection(componentStageData)
                    ? conversionDal.get(getSourceLayoutPointer(componentPointer))
                    : undefined
            })
        }

        if (!component?.components?.length || component.type !== 'Container') {
            return
        }

        if (component.isSlottedComponent) {
            updateMapWithChildren(contextHelper, componentPointer.id, {
                children: [...component.components].sort()
            })
            return
        }

        conversionDal.modify(componentPointer, (value: StructureEntryItem) => ({
            ...value,
            components: getOrderedChildren(componentStageData, contextHelper, readOnlyExtensionAPI)
        }))
    }

const headerLogic = (
    conversionDal: ConversionDal,
    contextHelper: PluginContext,
    startRow: number,
    startColumn: number = 1,
    maxElementsInFirstRow: number = 3
) => {
    let currentRow = startRow
    let currentColumn = startColumn
    let firstRowCounter = 0
    let elementsInFirstRow = 0
    contextHelper.set(HEADER_SPECIAL_CASE_KEY, false)
    return (childId: string) => {
        const childMeasurements = conversionDal.get(getMeasurementsPointer({id: childId}))
        const childStructure = conversionDal.get(getStructurePointer({id: childId}))
        const {width} = childMeasurements

        const isSmallElement = width <= HEADER_NO_DUPLICATE_WIDTH_THRESHOLD
        const isLogo = childStructure.semanticType === SemanticTypes.LOGO
        const isMenu = childStructure.semanticType === SemanticTypes.MENU
        const canFitInStartRow = firstRowCounter < maxElementsInFirstRow

        if (isLogo) {
            contextHelper.set(HEADER_SPECIAL_CASE_KEY, true)
            currentColumn++
            elementsInFirstRow++
            return {row: startRow, column: 1}
        }

        if (isMenu || (isSmallElement && canFitInStartRow)) {
            contextHelper.set(HEADER_SPECIAL_CASE_KEY, true)
            elementsInFirstRow++
            firstRowCounter++
            return {row: startRow, column: currentColumn++}
        }

        if (currentRow < 2 && elementsInFirstRow >= 1) currentRow = 2
        return {
            row: currentRow++,
            column: 1,
            columnEnd: currentColumn
        }
    }
}

export const positionItems = (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
    const {componentPointer, conversionDal} = componentStageData
    const component = conversionDal.get(componentPointer)

    if (!component?.components?.length || component.type !== 'Container' || component.isSlottedComponent) {
        return
    }

    const isInsideHeader = isHeaderContainer({conversionDal, componentPointer})
    const getRowAndColumnPredicate: RowPredicateFactory = (startRow: number, startColumn: number = 1) => {
        if (!isInsideHeader) {
            let currentRow = startRow
            return () => ({row: currentRow++, column: 1})
        }

        return headerLogic(conversionDal, contextHelper, startRow, startColumn)
    }

    processChildren(
        component.components ?? [],
        conversionDal,
        componentPointer,
        contextHelper,
        getRowAndColumnPredicate,
        isInsideHeader ? 2 : 1
    )
}

const stableStringify = (obj: any): string => {
    if (obj === null || typeof obj !== 'object') {
        return JSON.stringify(obj)
    }

    if (Array.isArray(obj)) {
        return `[${obj.map(value => stableStringify(value)).join(',')}]`
    }

    const keys = Object.keys(obj).sort()
    const pairs: string[] = keys.map(key => `${JSON.stringify(key)}:${stableStringify(obj[key])}`)
    return `{${pairs.join(',')}}`
}

const hashStructureMD5 = (structure: any) => md5(stableStringify(structure))

export const postStageOperation: PluginPostStageHandler = (
    {stageRuntimeStore, config}: BaseStageData,
    contextHelper: PluginContext
) => {
    if (config.mode !== AutoResponsivenessMode.Flush) {
        return
    }

    stageRuntimeStore.registerSkipPredicate(SkipPredicate.SHOULD_HALT_ALGO, () => {
        if (!contextHelper.get(ORDERED_GROUPED_STRUCTURE_HASH_KEY)) {
            contextHelper.set(
                ORDERED_GROUPED_STRUCTURE_HASH_KEY,
                hashStructureMD5(contextHelper.get(ORDERED_GROUPED_STRUCTURE_KEY))
            )
        }

        const hash = contextHelper.get(ORDERED_GROUPED_STRUCTURE_HASH_KEY)

        return !!hash && hash === config.lastGroupingHash
    })
}
